// ✅ TaskFlow Setup Sidebar – Final Corrected Version
// Includes Platform, Tenant, and Location Role Permission Matrices
// Works for Super Admin, Tenant Admin, and Location Admin (with correct permission checks)
// ✅ Updated on 2025-11-10 — Added Notification & Escalation Config submenu (non-breaking update)
// ✅ Updated on 2025-11-11 — Temporarily disabled submenu permission check for testing (to be re-enabled later)
// ✅ Updated on 2025-11-11 — Added collapsible submenu behavior + refined accessibility (non-breaking)

import React, { useEffect, useRef, useState, useContext } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  MdHomeFilled,
  MdGroup,
  MdSecurity,
  MdExtension,
  MdApartment,
  MdPlace,
  MdAccountTree,
  MdChevronLeft,
  MdMenu,
  MdShield,
} from "react-icons/md";
import { AuthContext } from "../context/AuthContext";
import "./sidebar.css";

const LS_KEY = "tf_sidebar_collapsed";

export default function SetupSidebar({ mobileOpen, setMobileOpen }) {
  const navigate = useNavigate();
  const { user, hasPermission } = useContext(AuthContext);

  // ✅ Safely derive role code (handles scopes[])
  const roleCode = (
    user?.role_code ||
    user?.scopes?.[0]?.role_code ||
    ""
  ).toUpperCase();

  const isSuperAdmin = roleCode === "SUPER_ADMIN";
  const isTenantAdmin = roleCode === "TENANT_ADMIN";
  const isLocationAdmin = roleCode === "LOCATION_ADMIN";

  const [collapsed, setCollapsed] = useState(
    localStorage.getItem(LS_KEY) === "true"
  );
  const [hoverExpand, setHoverExpand] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  const sidebarRef = useRef(null);

  // ✅ Submenu state handler (for collapsible groups)
  const [openGroups, setOpenGroups] = useState({});
  const toggleGroup = (key) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 992);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    localStorage.setItem(LS_KEY, collapsed);
  }, [collapsed]);

  const expanded =
    !collapsed || hoverExpand || (isMobile && mobileOpen === true);

  // ============================================================
  // ✅ Menu Configuration
  // ============================================================
  const menu = [
    {
      key: "dashboard",
      label: "Back to Dashboard",
      to: "/dashboard",
      icon: <MdHomeFilled size={22} />,
      isPrimary: true,
    },
    {
      key: "users",
      label: "Users",
      to: "/setup/users",
      icon: <MdGroup size={22} />,
      permission: "USER_MANAGE:view",
    },
    {
      key: "user-security",
      label: "User Permission Overrides",
      to: "/setup/user-security",
      icon: <MdShield size={22} />,
      permission: "USER_SECURITY:view",
    },

    {
      key: "platform-roles",
      label: "Platform Role Master",
      to: "/setup/rolesmaster/platform",
      icon: <MdSecurity size={22} />,
      superAdminOnly: true,
    },
    {
      key: "role-master-permission-matrix",
      label: "Role Master Permission Matrix",
      to: "/setup/role-master-permission",
      icon: <MdShield size={22} />,
      superAdminOnly: true,
    },
    {
      key: "tenant-role-permission-matrix",
      label: "Tenant Role Permission Matrix",
      to: "/setup/tenant-role-permission",
      icon: <MdShield size={22} />,
      permission: "ROLE_PERMISSION_MANAGE:view",
    },
    {
      key: "location-role-permission-matrix",
      label: "Location Role Permission Matrix",
      to: "/setup/location-role-permission",
      icon: <MdShield size={22} />,
      permission: "LOCATION_ROLE_PERMISSION_MANAGE:view",
    },
    {
      key: "functionality-master",
      label: "Functionality Master",
      to: "/setup/functionality",
      icon: <MdExtension size={22} />,
      superAdminOnly: true,
    },
    {
      key: "tenants",
      label: "Tenants",
      to: "/setup/tenants",
      icon: <MdApartment size={22} />,
      permission: "TENANT_MANAGE:view",
    },
    {
      key: "locations",
      label: "Locations",
      to: "/setup/locations",
      icon: <MdPlace size={22} />,
      permission: "LOCATION_MANAGE:view",
    },
    {
      key: "departments",
      label: "Departments",
      to: "/setup/departments",
      icon: <MdAccountTree size={22} />,
      permission: "DEPARTMENT_MANAGE:view",
    },

    // ✅ Added on 2025-11-10 — Escalation & Notification Config submenu
    {
      key: "notification-escalation-config",
      label: "Notification & Escalation Config",
      icon: <MdShield size={22} />,
      submenu: [
        {
          key: "escalation-rules",
          label: "Escalation Rules",
          to: "/setup/escalation/rules",
          permission: "ESCALATION_RULE:view",
        },
        {
          key: "notification-channels",
          label: "Notification Channels",
          to: "/setup/escalation/channels",
          permission: "NOTIFICATION_CHANNEL:view",
        },
        {
          key: "user-notification-preferences",
          label: "User Notification Preferences",
          to: "/setup/escalation/preferences",
          permission: "USER_NOTIFICATION_PREFERENCE:view",
        },
        {
          key: "escalation-history",
          label: "Escalation History",
          to: "/setup/escalation/history",
          permission: "ESCALATION_HISTORY:view",
        },
      ],
    },
  ];

  // ============================================================
  // ✅ Sidebar Handlers
  // ============================================================
  const handleCollapseToggle = () => {
    if (isMobile) return setMobileOpen(false);
    setCollapsed((c) => !c);
  };

  const handleHomeClick = () => {
    navigate("/dashboard");
    if (isMobile) setMobileOpen(false);
  };

  // ============================================================
  // ✅ Render
  // ============================================================
  return (
    <>
      {isMobile && mobileOpen && (
        <div
          className="tf-sidebar-backdrop"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        ref={sidebarRef}
        className={`tf-sidebar ${expanded ? "expanded" : "collapsed"} ${
          isMobile && mobileOpen ? "open" : ""
        }`}
        onMouseLeave={() => setHoverExpand(false)}
      >
        <div
          className="tf-sidebar-hover-edge"
          onMouseEnter={() => collapsed && !isMobile && setHoverExpand(true)}
        />

        {/* ✅ TF Logo */}
        <div className="tf-sidebar-header">
          <div className="tf-logo">
            <span className="tf-logo-icon">TF</span>
            {expanded && <span className="tf-logo-text">TaskFlow</span>}
          </div>

          <button
            className="tf-icon-btn"
            onClick={handleCollapseToggle}
            onMouseEnter={() =>
              collapsed && !isMobile && setHoverExpand(true)
            }
          >
            {isMobile ? <MdMenu /> : <MdChevronLeft />}
          </button>
        </div>

        {/* ✅ Navigation Menu */}
        <nav className="tf-nav">
          {menu.map((m) => {
            if (m.superAdminOnly && !isSuperAdmin) return null;

            if (m.permission) {
              const [func, action] = m.permission.split(":");
              if (!hasPermission(func, action)) return null;
            }

            if (m.key === "tenant-role-permission-matrix" && isLocationAdmin)
              return null;

            if (m.isPrimary && !expanded) {
              return (
                <button
                  key={m.key}
                  className="tf-home-icon"
                  onClick={handleHomeClick}
                  title={m.label}
                >
                  {m.icon}
                </button>
              );
            }

            if (m.isPrimary && expanded) {
              return (
                <button
                  key={m.key}
                  className="tf-back-primary"
                  onClick={handleHomeClick}
                >
                  {m.icon}
                  <span className="tf-label">{m.label}</span>
                </button>
              );
            }

            // ✅ Handle submenu groups (2025-11-10 + collapsible fix 2025-11-11)
            if (m.submenu && m.submenu.length > 0) {
              const isOpen = openGroups[m.key];

              return (
                <div key={m.key} className="tf-submenu-group">
                  <div
                    className="tf-nav-item group-header"
                    onClick={() => toggleGroup(m.key)}
                  >
                    <span className="tf-icon">{m.icon}</span>
                    {expanded && (
                      <span className="tf-label">
                        {m.label} {isOpen ? "▾" : "▸"}
                      </span>
                    )}
                  </div>
                  {expanded && (
                    <div className={`tf-submenu ${isOpen ? "open" : ""}`}>
                      {m.submenu.map((sub) => {
                        const [func, action] = (sub.permission || "").split(":");

                        // 🧩 TEMPORARY CHANGE — 2025-11-11
                        // Commented out permission validation to debug submenu visibility.
                        // When backend Role → Function permissions are fully configured,
                        // uncomment the next two lines to restore original behavior.
                        //
                        // if (sub.permission && !hasPermission(func, action))
                        //   return null;

                        return (
                          <NavLink
                            key={sub.key}
                            to={sub.to}
                            className={({ isActive }) =>
                              `tf-nav-item tf-subitem ${
                                isActive ? "active" : ""
                              }`
                            }
                            onClick={() => isMobile && setMobileOpen(false)}
                          >
                            <span className="tf-label">{sub.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={m.key}
                to={m.to}
                className={({ isActive }) =>
                  `tf-nav-item ${isActive ? "active" : ""}`
                }
                title={!expanded ? m.label : ""}
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="tf-icon">{m.icon}</span>
                {expanded && <span className="tf-label">{m.label}</span>}
                <span className="tf-active-indicator" />
              </NavLink>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
